import type { TOrderInvoiceBlockReason } from '@team-milva/lab-utils/orderInvoiceBlocking'
import { t } from '@/locales'
import { getOrderInvoiceBlockReasonLabel } from '@/globals/typescript/utils/orderInvoiceBlockReasonLabels'

/**
 * A failure from the BC push starts with a tag the backend puts there (`bcErrorTags.ts` in
 * lab-cloud, which owns the list of codes):
 *
 *  - `[<code>] text` — a mistake in Milva's own setup the lab can fix. Shown as a Danish line; the
 *    English `text` stays available behind "Vis detaljer". A code may carry parameters:
 *    `[order-blocked:missing-prices,missing-currency] text`.
 *  - `[bc] text` — BC's own answer. Shown under a short heading, with BC's text behind "Vis BC's
 *    besked".
 *  - no tag, or a code this app does not know — shown exactly as received, so no failure is hidden.
 *
 * Only the tag is ever read; the text after it is never parsed.
 */

export interface IParsedBcError {
  kind: 'milva' | 'bc' | 'plain'
  code: string
  params: string[]
  text: string
}

const TAG = /^\[([a-z0-9-]+)(?::([^\]]*))?\]\s*([\s\S]*)$/

export function parseBcErrorTag(raw: string): IParsedBcError {
  const match = TAG.exec(raw)
  if (!match) {
    return { kind: 'plain', code: '', params: [], text: raw }
  }
  const [, code = '', params = '', text = ''] = match
  return {
    kind: code === 'bc' ? 'bc' : 'milva',
    code,
    params: params ? params.split(',') : [],
    text,
  }
}

/** The Danish line for one of Milva's own error codes, or `null` for a code this app does not know. */
function milvaErrorLine(code: string, params: string[]): string | null {
  switch (code) {
    case 'payer-missing':
      return t('W: Vælg, hvem fakturaen skal sendes til, før den kan oprettes.')
    case 'order-blocked':
      return params.length > 0
        ? t('W: Ordren kan ikke faktureres endnu: {reasons}', {
            reasons: params.map((reason) => getOrderInvoiceBlockReasonLabel(reason as TOrderInvoiceBlockReason)).join(', '),
          })
        : t('W: Ordren kan ikke faktureres endnu.')
    case 'partner-inactive':
      return t('W: Forsikringskunden er inaktiv. Genaktivér den, eller vælg en anden betaler.')
    case 'partner-bc-number-missing':
      return t('W: Forsikringskunden har ikke et Business Central-kundenummer. Tilføj det på forsikringskunden, før der faktureres.')
    case 'customer-bc-number-missing':
      return t('W: Kunden har ikke et Business Central-kundenummer. Tilføj det på kunden, før der faktureres.')
    case 'customer-bc-number-unknown':
      return t('W: Kundenummeret findes ikke i Business Central. Ret det på kunden, før der faktureres.')
    case 'bc-foreign-invoice':
      return t('W: Business Central har allerede en bogført faktura med samme «Deres reference» som denne ordre (ordrenummeret). Den er ikke overtaget, og der oprettes ingen ny. Åbn den i Business Central for at se, om den hører til denne ordre.')
    case 'extension-failed':
      return t(
        'W: En tilpasning til denne lab mislykkedes, så fakturaen er ikke bogført. Kladden i Business Central er bevaret. Prøv igen, eller kontakt Milva, hvis det bliver ved.'
      )
    case 'bc-draft-mismatch':
      return t(
        'W: Business Central har allerede en kladde med samme «Deres reference» som denne ordre (ordrenummeret), men dens linjer passer ikke til ordren. Kladden er hverken overtaget eller ændret. Åbn den i Business Central, slet den, og tryk derefter «Prøv igen».'
      )
    case 'bc-other-connection':
      return t(
        'W: Fakturaen er lavet på en anden Business Central-forbindelse end den, laboratoriet er forbundet til her. Der er ikke gjort noget, og intet er sendt til Business Central.'
      )
    case 'posted-not-sent':
      return t('W: Fakturaen er bogført i Business Central, men det er ikke bekræftet, at den er sendt. Brug «Send igen».')
    case 'currency-differs':
      return params.length === 2
        ? t('W: Ordren er i {order}, men kunden står i {card} i Business Central. Automatisk fakturering sender den ikke — tjek valutaen, og send fakturaen selv.', {
            order: params[0] ?? '',
            card: params[1] ?? '',
          })
        : t('W: Ordrens valuta afviger fra kundekortet i Business Central. Automatisk fakturering sender den ikke — tjek valutaen, og send fakturaen selv.')
    default:
      return null
  }
}

export interface IDescribedBcError {
  /** What the user reads first. For an unclassified failure this is the raw text. */
  line: string
  /** The original text, offered behind a fold. Empty when there is nothing more to show. */
  detail: string
  /** Label of the fold, matching what the detail is. */
  detailLabel: string
}

export function describeBcError(raw: string): IDescribedBcError {
  const parsed = parseBcErrorTag(raw)

  if (parsed.kind === 'bc') {
    return {
      line: t('W: Business Central afviste handlingen.'),
      detail: parsed.text,
      detailLabel: t("W: Vis BC's besked"),
    }
  }

  if (parsed.kind === 'milva') {
    const line = milvaErrorLine(parsed.code, parsed.params)
    if (line) {
      return { line, detail: parsed.text, detailLabel: t('W: Vis detaljer') }
    }
  }

  // Untagged, or a code this app does not know: show it as it came, nothing folded away.
  return { line: parsed.kind === 'plain' ? raw : parsed.text || raw, detail: '', detailLabel: '' }
}
